import * as React from "react"
import {
    ColumnDef,
    ColumnFiltersState,
    SortingState,
    VisibilityState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table"
import {ArrowUpDown, MoreHorizontal} from "lucide-react"

import {Button} from "@/components/ui/button"

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {Input} from "@/components/ui/input"
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"
import {useEffect, useState} from "react";
import CreateUser from "@/components/create-user.tsx";
import Restricted from "@/components/permissions/Restricted.tsx";
import {useNavigate} from "react-router";
import {keycloakAdminApi} from "@/main.tsx";
import {useAuth} from "react-oidc-context";
import axios from "axios";

export type User = {
    id: string
    ai_attachment_count: number
    username: string
    firstName: string
    lastName: string
    email: string
    emailVerified: boolean
    attributes: {
        locale: string[]
    }
    createdTimestamp: number
    enabled: boolean
    totp: boolean
    disableableCredentialTypes: any[]
    requiredActions: any[]
    notBefore: number
    access: {
        manageGroupMembership: boolean
        view: boolean
        mapRoles: boolean
        impersonate: boolean
        manage: boolean
    }
}

const deleteUser = (id: string) => {
    keycloakAdminApi.delete(`/users/${id}`).then(() => {
        window.location.reload()
    })
}

export const columns: ColumnDef<User>[] = [
    {
        accessorKey: "name",
        header: "Név",
        cell: ({row}) => <div>{row.getValue('name')}</div>,
    },
    {
        accessorKey: "email",
        header: ({column}) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Email
                    <ArrowUpDown/>
                </Button>
            )
        },
        cell: ({row}) => <div className="lowercase">{row.getValue('email')}</div>,
    },
    {
        accessorKey: "ai_attachment_count",
        header: ({column}) => {
            return (
                <Button
                    className={"w-36"}
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    AI cégértékelők
                    <ArrowUpDown/>
                </Button>
            )
        },
        cell: ({row}) => {
            const item = row.original
            return (
                <div className="lowercase w-36 text-center">{item.ai_attachment_count}</div>
            )
        },
    },
    {
        id: "actions",
        enableHiding: false,
        cell: ({row}) => {
            const item = row.original
            const navigate = useNavigate()
            const auth = useAuth()
            return (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Menü kinyitása</span>
                            <MoreHorizontal/>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Műveletek</DropdownMenuLabel>

                        <DropdownMenuItem
                            onClick={() => {
                                navigate(`/database/users/${item.id}`)
                            }}
                        >
                            Szerkesztés
                        </DropdownMenuItem>
                        <DropdownMenuItem className={"text-destructive"} onClick={() => {
                            keycloakAdminApi.post(`/users/${item.id}/impersonation`).then(() => {
                                auth.signinRedirect()
                            })
                        }}>Impersonate</DropdownMenuItem>
                        <DropdownMenuSeparator/>

                        <DropdownMenuItem className={"text-destructive"}
                                          onClick={() => deleteUser(item.id)}>Törlés</DropdownMenuItem>


                    </DropdownMenuContent>
                </DropdownMenu>
            )
        },
    },
]

export default function Users() {
    const [data, setData] = useState<User[]>([])

    useEffect(() => {
        keycloakAdminApi.get(`/users`).then((res) => {
            axios.get('/api/users').then((laravel_users) => {
                const users = res.data.map((x: any) => {
                    return {...x, name: x.firstName + " " + x.lastName, ai_attachment_count: (laravel_users.data.filter((y: any) => y.id == x.id)?.[0]?.ai_attachment_count) ?? 0}
                })
                setData(users)
            })
        })
    }, []);

    const [sorting, setSorting] = React.useState<SortingState>([])
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
        []
    )
    const [columnVisibility, setColumnVisibility] =
        React.useState<VisibilityState>({})
    const [rowSelection, setRowSelection] = React.useState({})

    const table = useReactTable({
        data,
        columns,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
        },
    })


    return (
        <div className="w-full px-4">
            <div className="flex items-center justify-between py-4">
                <Input
                    placeholder="Felhasználók keresése..."
                    value={(table.getColumn("email")?.getFilterValue() as string) ?? ""}
                    onChange={(event) =>
                        table.getColumn("email")?.setFilterValue(event.target.value)
                    }
                    className="max-w-sm"
                />
                <Restricted to={"user.any"}>
                    <CreateUser/>
                </Restricted>
            </div>
            <Restricted to={"user.any"}>
                <div className="rounded-md">
                    <Table>
                        <TableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => {
                                        return (
                                            <TableHead key={header.id}>
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                            </TableHead>
                                        )
                                    })}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {table.getRowModel().rows?.length ? (
                                table.getRowModel().rows.map((row) => (
                                    <TableRow
                                        key={row.id}
                                        data-state={row.getIsSelected() && "selected"}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <TableCell key={cell.id}>
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length}
                                        className="h-24 text-center"
                                    >
                                        Nincs találat.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
                <div className="flex items-center justify-end space-x-2 py-4">

                    <div className="space-x-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => table.previousPage()}
                            disabled={!table.getCanPreviousPage()}
                        >
                            Előző
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => table.nextPage()}
                            disabled={!table.getCanNextPage()}
                        >
                            Következő
                        </Button>
                    </div>
                </div>
            </Restricted>
        </div>
    )
}